import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Inbox, Trash2 } from "lucide-react";

const STATUSES = ["New", "Contacted", "In Progress", "Completed", "Archived"];
const STATUS_STYLE = {
  New: "bg-[#EAF2FE] text-[#1558c0]", Contacted: "bg-[#FFF4E5] text-[#B26A00]",
  "In Progress": "bg-[#EDE7F6] text-[#5E35B1]", Completed: "bg-[#E8F5E9] text-[#2E7D32]",
  Archived: "bg-[var(--alt)] text-[var(--ink-2)]",
};
const inputCls = "px-3 py-2 border border-[var(--line)] outline-none text-sm rounded-md";

function EnquiryDialog({ item, onChanged, trigger }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(item.status);
  const [notes, setNotes] = useState(item.internal_notes || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.put(`/admin/enquiries/${item.id}`, { status, internal_notes: notes }); toast.success("Saved"); setOpen(false); onChanged(); }
    catch { toast.error("Could not save"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-xl">
        <DialogHeader><DialogTitle className="font-display">{item.service_type} — {item.full_name}</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <div><span className="text-[var(--ink-2)]">Business:</span> {item.business_name || "—"}</div>
            <div><span className="text-[var(--ink-2)]">Phone:</span> {item.phone}</div>
            <div className="col-span-2"><span className="text-[var(--ink-2)]">Email:</span> <a href={`mailto:${item.email}`} className="text-[#1E6FEB]">{item.email}</a></div>
            {item.website && <div className="col-span-2"><span className="text-[var(--ink-2)]">Website/social:</span> {item.website}</div>}
            <div className="col-span-2 text-xs text-[var(--ink-2)]">Received {new Date(item.created_at).toLocaleString()} · GDPR consent: {item.gdpr_consent ? "Yes" : "No"} · Marketing: {item.marketing_consent ? "Yes" : "No"}</div>
          </div>
          <div className="border-t border-[var(--line)] pt-2 mt-2">
            <p className="font-semibold mb-1">Enquiry details</p>
            {Object.entries(item.form_data || {}).map(([k, v]) => (
              <div key={k} className="mb-1"><span className="text-[var(--ink-2)]">{k}:</span> {String(v)}</div>
            ))}
            {item.file_url && <a href={`${api.defaults.baseURL}/enquiries/file/${item.file_url}`} target="_blank" rel="noreferrer" className="text-[#1E6FEB] underline text-sm">Download attached file</a>}
          </div>
          <div className="border-t border-[var(--line)] pt-3 mt-2 space-y-2">
            <div><label className="text-xs font-semibold text-[var(--ink-2)] block mb-1">Status</label>
              <select data-testid="enq-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} w-full`}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
            <div><label className="text-xs font-semibold text-[var(--ink-2)] block mb-1">Internal notes</label>
              <textarea data-testid="enq-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className={`${inputCls} w-full`} /></div>
            <button onClick={save} disabled={saving} data-testid="enq-save" className="w-full bg-[var(--accent)] text-white font-semibold py-2.5 rounded-md disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EnquiriesManager() {
  const [items, setItems] = useState([]);
  const [service, setService] = useState("");
  const [status, setStatus] = useState("");
  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (service) p.set("service", service);
    if (status) p.set("status", status);
    api.get(`/admin/enquiries${p.toString() ? `?${p}` : ""}`).then((r) => setItems(r.data)).catch(() => {});
  }, [service, status]);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => { if (!window.confirm("Delete this enquiry?")) return; await api.delete(`/admin/enquiries/${id}`); toast.success("Deleted"); load(); };
  const services = [...new Set(items.map((i) => i.service_type))];

  return (
    <div data-testid="enquiries-manager">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="inline-flex items-center gap-2 text-[var(--ink-2)]"><Inbox size={18} /><span className="font-semibold">{items.length} enquiries</span></div>
        <select data-testid="enq-filter-service" value={service} onChange={(e) => setService(e.target.value)} className={inputCls}>
          <option value="">All services</option>
          {services.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select data-testid="enq-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto border border-[var(--line)] rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-[var(--alt)] text-left text-xs uppercase tracking-wide text-[var(--ink-2)]">
            <tr><th className="px-4 py-3">Service</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Received</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className="border-t border-[var(--line)]" data-testid={`enquiry-row-${e.id}`}>
                <td className="px-4 py-3 font-medium">{e.service_type}</td>
                <td className="px-4 py-3">{e.full_name}<div className="text-xs text-[var(--ink-2)]">{e.business_name}</div></td>
                <td className="px-4 py-3 text-xs">{e.email}<br />{e.phone}</td>
                <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded ${STATUS_STYLE[e.status] || ""}`}>{e.status}</span></td>
                <td className="px-4 py-3 text-xs text-[var(--ink-2)] whitespace-nowrap">{new Date(e.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <EnquiryDialog item={e} onChanged={load} trigger={<button data-testid={`enq-view-${e.id}`} className="text-[#1E6FEB] hover:underline mr-3">View</button>} />
                  <button onClick={() => remove(e.id)} className="text-[#B71C1C] hover:underline"><Trash2 size={14} className="inline" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[var(--ink-2)]">No enquiries yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
